import * as Phaser from 'phaser';
import { PaintedScene, type PaintedSceneInitData } from './PaintedScene';
import type { PaintedSceneAssetSpec } from './sceneAssetSpec.schema';
import type { JourneyManifest } from './journeyManifest.schema';

export const HILLTOP_VISTA_SCENE_PHASER_KEY = 'HilltopVistaScene' as const;
export const HILLTOP_VISTA_SCENE_MANIFEST_KEY = 'scene-5-hilltop-vista' as const;

type WalkerKeys = {
  W: Phaser.Input.Keyboard.Key;
  A: Phaser.Input.Keyboard.Key;
  S: Phaser.Input.Keyboard.Key;
  D: Phaser.Input.Keyboard.Key;
  E: Phaser.Input.Keyboard.Key;
};

type DevCamSnapshot = { scrollX: number; scrollY: number; zoom: number };

type DevCamKeys = {
  toggle: Phaser.Input.Keyboard.Key;
  reset: Phaser.Input.Keyboard.Key;
  zoomIn: Phaser.Input.Keyboard.Key;
  zoomInNumpad: Phaser.Input.Keyboard.Key;
  zoomOut: Phaser.Input.Keyboard.Key;
  zoomOutNumpad: Phaser.Input.Keyboard.Key;
  panUp: Phaser.Input.Keyboard.Key;
  panDown: Phaser.Input.Keyboard.Key;
  panLeft: Phaser.Input.Keyboard.Key;
  panRight: Phaser.Input.Keyboard.Key;
  shift: Phaser.Input.Keyboard.Key;
};

const DEV_CAM_PAN_PX_PER_SEC = 400;
const DEV_CAM_ZOOM_PER_SEC = 0.6;
const DEV_CAM_WHEEL_ZOOM_STEP = 0.08;
const DEV_CAM_MIN_ZOOM = 0.2;
const DEV_CAM_MAX_ZOOM = 4;

export class HilltopVistaScene extends PaintedScene {
  private devCamActive = false;
  private devCamKeys: DevCamKeys | null = null;
  private devCamSnapshot: DevCamSnapshot | null = null;
  private devCamHud: Phaser.GameObjects.Text | null = null;
  private devCamWheelHandler: ((pointer: Phaser.Input.Pointer, _gameObjects: unknown, _dx: number, dy: number) => void) | null = null;
  private suppressedCursors: Phaser.Types.Input.Keyboard.CursorKeys | undefined;
  private suppressedKeys: WalkerKeys | undefined;
  private suppressedKeysSnapshotted = false;

  constructor() {
    super(HILLTOP_VISTA_SCENE_PHASER_KEY, HILLTOP_VISTA_SCENE_MANIFEST_KEY);
  }

  protected provideAssetSpec(): PaintedSceneAssetSpec | null {
    return null;
  }

  protected provideManifest(): JourneyManifest | null {
    return null;
  }

  init(data?: PaintedSceneInitData): void {
    super.init(data);
  }

  create(): void {
    super.create();
    if (import.meta.env.DEV) {
      this.bindDevCam();
    }
  }

  update(time: number, delta: number): void {
    super.update(time, delta);
    if (this.devCamActive) {
      this.tickDevCam(delta);
    }
  }

  private bindDevCam(): void {
    if (!this.input.keyboard) return;
    const kb = this.input.keyboard;
    this.devCamKeys = {
      toggle: kb.addKey(Phaser.Input.Keyboard.KeyCodes.F2),
      reset: kb.addKey(Phaser.Input.Keyboard.KeyCodes.R),
      zoomIn: kb.addKey(Phaser.Input.Keyboard.KeyCodes.PLUS),
      zoomInNumpad: kb.addKey(Phaser.Input.Keyboard.KeyCodes.NUMPAD_ADD),
      zoomOut: kb.addKey(Phaser.Input.Keyboard.KeyCodes.MINUS),
      zoomOutNumpad: kb.addKey(Phaser.Input.Keyboard.KeyCodes.NUMPAD_SUBTRACT),
      panUp: kb.addKey(Phaser.Input.Keyboard.KeyCodes.UP),
      panDown: kb.addKey(Phaser.Input.Keyboard.KeyCodes.DOWN),
      panLeft: kb.addKey(Phaser.Input.Keyboard.KeyCodes.LEFT),
      panRight: kb.addKey(Phaser.Input.Keyboard.KeyCodes.RIGHT),
      shift: kb.addKey(Phaser.Input.Keyboard.KeyCodes.SHIFT),
    };

    kb.on('keydown-F2', this.toggleDevCam, this);

    this.devCamWheelHandler = (_pointer, _gameObjects, _dx, dy) => {
      if (!this.devCamActive) return;
      const cam = this.cameras.main;
      const factor = dy > 0 ? 1 - DEV_CAM_WHEEL_ZOOM_STEP : 1 + DEV_CAM_WHEEL_ZOOM_STEP;
      const next = Phaser.Math.Clamp(cam.zoom * factor, DEV_CAM_MIN_ZOOM, DEV_CAM_MAX_ZOOM);
      cam.setZoom(next);
    };
    this.input.on('wheel', this.devCamWheelHandler);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.teardownDevCam();
    });
  }

  private toggleDevCam(): void {
    if (this.devCamActive) {
      this.exitDevCam();
    } else {
      this.enterDevCam();
    }
  }

  private enterDevCam(): void {
    const cam = this.cameras.main;
    this.devCamSnapshot = { scrollX: cam.scrollX, scrollY: cam.scrollY, zoom: cam.zoom };

    // Yank walker input so WASD/arrows drive the camera, not the player.
    this.suppressedCursors = this.data.get('painted-cursors') as
      | Phaser.Types.Input.Keyboard.CursorKeys
      | undefined;
    this.suppressedKeys = this.data.get('painted-keys') as WalkerKeys | undefined;
    this.suppressedKeysSnapshotted = true;
    this.data.set('painted-cursors', undefined);
    this.data.set('painted-keys', undefined);

    this.devCamHud = this.add
      .text(8, 8, '', {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: '#ffffff',
        backgroundColor: 'rgba(0,0,0,0.55)',
        padding: { left: 6, right: 6, top: 4, bottom: 4 },
      })
      .setScrollFactor(0)
      .setDepth(10_000);
    this.devCamActive = true;
    this.updateDevCamHud();
  }

  private exitDevCam(): void {
    if (this.devCamSnapshot) {
      const cam = this.cameras.main;
      cam.setScroll(this.devCamSnapshot.scrollX, this.devCamSnapshot.scrollY);
      cam.setZoom(this.devCamSnapshot.zoom);
    }
    this.devCamSnapshot = null;

    if (this.suppressedKeysSnapshotted) {
      this.data.set('painted-cursors', this.suppressedCursors);
      this.data.set('painted-keys', this.suppressedKeys);
      this.suppressedCursors = undefined;
      this.suppressedKeys = undefined;
      this.suppressedKeysSnapshotted = false;
    }

    this.devCamHud?.destroy();
    this.devCamHud = null;
    this.devCamActive = false;
  }

  private tickDevCam(deltaMs: number): void {
    if (!this.devCamKeys) return;
    const k = this.devCamKeys;
    const cam = this.cameras.main;
    const seconds = deltaMs / 1000;
    const speedMult = k.shift.isDown ? 3 : 1;
    const pan = (DEV_CAM_PAN_PX_PER_SEC * seconds * speedMult) / cam.zoom;

    let dx = 0;
    let dy = 0;
    if (k.panLeft.isDown) dx -= pan;
    if (k.panRight.isDown) dx += pan;
    if (k.panUp.isDown) dy -= pan;
    if (k.panDown.isDown) dy += pan;
    if (dx !== 0 || dy !== 0) {
      cam.scrollX += dx;
      cam.scrollY += dy;
    }

    let zoomDelta = 0;
    if (k.zoomIn.isDown || k.zoomInNumpad.isDown) zoomDelta += DEV_CAM_ZOOM_PER_SEC * seconds * speedMult;
    if (k.zoomOut.isDown || k.zoomOutNumpad.isDown) zoomDelta -= DEV_CAM_ZOOM_PER_SEC * seconds * speedMult;
    if (zoomDelta !== 0) {
      const next = Phaser.Math.Clamp(cam.zoom + zoomDelta, DEV_CAM_MIN_ZOOM, DEV_CAM_MAX_ZOOM);
      cam.setZoom(next);
    }

    if (Phaser.Input.Keyboard.JustDown(k.reset) && this.devCamSnapshot) {
      cam.setScroll(this.devCamSnapshot.scrollX, this.devCamSnapshot.scrollY);
      cam.setZoom(this.devCamSnapshot.zoom);
    }

    this.updateDevCamHud();
  }

  private updateDevCamHud(): void {
    if (!this.devCamHud) return;
    const cam = this.cameras.main;
    const lines = [
      'DEV CAM  (F2 exit)',
      `scrollX ${cam.scrollX.toFixed(1)}  scrollY ${cam.scrollY.toFixed(1)}`,
      `zoom    ${cam.zoom.toFixed(3)}`,
      'arrows/WASD pan · wheel or +/- zoom · shift faster · R reset',
    ];
    this.devCamHud.setText(lines.join('\n'));
  }

  private teardownDevCam(): void {
    if (this.devCamActive) this.exitDevCam();
    if (this.devCamWheelHandler) {
      this.input.off('wheel', this.devCamWheelHandler);
      this.devCamWheelHandler = null;
    }
    if (this.input.keyboard) {
      this.input.keyboard.off('keydown-F2', this.toggleDevCam, this);
    }
    this.devCamKeys = null;
  }
}
